import React, { useState, useEffect } from "react";
import HeaderBar from "../HeaderBar";
import useStateMachineWrapper from "../../helpers/useStateMachineWrapper";
import useCalculators from "../../helpers/useCalculators";
import { displaySpeed } from "../../helpers/strings";
import { dataLayerPush } from "../../helpers/analytics";
import SpeedDetails from "../SpeedDetails";
import BroadbandFacts from "../BroadbandFacts";
import MobileNextButton from "../MobileNextButton";

const ChooseInternet = () => {
  const { actions, state } = useStateMachineWrapper();
  const { getApplicablePromo, getSubtotal, getCartItems } = useCalculators();

  const groupOptions = Array.from(
    new Map(
      (state.session.availableServices?.promos || [])
        .map(promo => {
          if (!promo?.group?.id || promo.group.id == 1) {
            return null;
          }
          return [promo.group.id, promo.group];
        })
        .filter(Boolean)
    ).values()
  ).sort((a, b) => {
    const aPriority = parseInt(a.priority ?? 999, 10);
    const bPriority = parseInt(b.priority ?? 999, 10);
    return aPriority - bPriority;
  });

  useEffect(() => {
    if (!state.session.promoGroupID && groupOptions.length > 0) {
      const firstGroupId = groupOptions[0].id;
      actions.update({ promoGroupID: firstGroupId });
  
      const promo = getApplicablePromo({
        speed: state.session.speed,
        groupId: firstGroupId
      });
  
      actions.update({
        promo,
        promoID: promo?.id || '',
        promoName: promo?.description || ''
      });
    }
    else{
      if(groupOptions.length === 0) {
        actions.update({ promoGroupID: 0});
        const promo = getApplicablePromo({
          speed: state.session.speed,
          groupId: 0
        });
    
        actions.update({
          promo,
          promoID: promo?.id || '',
          promoName: promo?.description || ''
        });
      }
    }
  }, [groupOptions, state.session.promoGroupID, state.session.speed]);

  useEffect(() => {
    if (
      state.session.speed &&
      !availableSpeeds.some(speed => speed.download === state.session.speed)
    ) {
      actions.update({ speed: '' });
    }
  }, [availableSpeeds, state.session.speed]);

  const selectGroup = (val) => {
    actions.update({ promoGroupID: val });
  
    const applicablePromo = getApplicablePromo({
      speed: state.session.speed,
      groupId: val
    });
  
    // Get speeds for the selected group
    const validSpeeds = (state.session.availableServices?.internet || [])
      .map(tier => tier?.[val])
      .filter(Boolean)
      .filter(speed => {
        const promo = getApplicablePromo({
          speed: speed.download,
          groupId: val
        });
        return !!promo;
      });
  
    // Check if current speed is valid in new group
    const isValidSpeed = validSpeeds.some(
      speed => speed.download === state.session.speed
    );
  
    if (!isValidSpeed) {
      actions.update({ speed: '', speedAmount: '', promo: null, promoID: '', promoName: '' });
    } else if (applicablePromo) {
      actions.update({
        promo: applicablePromo,
        promoID: applicablePromo.id,
        promoName: applicablePromo.description
      });
    } else {
      actions.update({ promo: null, promoID: '', promoName: '' });
    }
  };
  

  useEffect(() => {
   // console.log('current speed - ',state.session.speed, '...');
    if (state.session.speed) {
      const promo = getApplicablePromo({
        speed: state.session.speed,
        groupId: state.session.promoGroupID
      });
      if (promo) {
        actions.update({
          promo,
          promoID: promo.id,
          promoName: promo.description
        });
      }
      
    }
  }, []);

  const selectSpeed = (val) => {
    actions.update({
      speed: val.download,
      speedAmount: val.amount,
    });
    if (val.download === '8000') {
      actions.update({ pods: 0 });
    }

    const promo = getApplicablePromo({
      speed: val.download,
      groupId: state.session.promoGroupID
    });

    let costForGA = val.amount;
    if (promo) {
      actions.update({
        promo,
        promoID: promo.id,
        promoName: promo.description
      });
      const internetDiscount = parseFloat(promo.discount.internet || 0);
      if (internetDiscount > 0) {
        const adjusted = Math.round((val.amount - internetDiscount) * 100) / 100;
        costForGA = adjusted;
      }
    } else {
      actions.update({ promo: null, promoID: '', promoName: '' });
    }

    dataLayerPush({
      event: 'change_speed',
      Speed: val.download,
      speedAmount: costForGA,
    });
    
  };

  const promoGroupID = state.session.promoGroupID?.toString();

  const allSpeeds = promoGroupID
  ? (state.session.availableServices?.internet || [])
      .map(tier => tier?.[promoGroupID])
      .filter(Boolean)
      .filter(speed => {
        const promo = getApplicablePromo({
          speed: speed.download,
          groupId: parseInt(promoGroupID)
        });
        return !!promo; // only include speeds with applicable promo
      })
  : [];

const availableSpeeds = allSpeeds.sort((a, b) => {
  if (a.download === '8000') return 1;
  if (b.download === '8000') return -1;
  return b.amount - a.amount;
});


  const shouldUseThirds = availableSpeeds.length !== 4;
  const speedSizeClass = shouldUseThirds ? 'sm:basis-[30%]' : 'sm:basis-[45%]';

  const BADGE_STYLES = {
    1: {
      bgColor: 'bg-[#8100A8]',
      textColor: 'text-white',
      shapeClass: 'rounded-t-md', // square with top rounded
    },
    2: {
      bgColor: 'bg-[#FFC72D]',
      textColor: 'text-black',
      shapeClass: 'rounded-t-md', // square with top rounded
    },
    3: {
      bgColor: 'bg-[#FFC72D]',
      textColor: 'text-black',
      shapeClass: 'h-[200px] rounded-full -z-1',  // circular
    },
    4: {
      bgColor: 'bg-[#8100A8]',
      textColor: 'text-white',
      shapeClass: 'h-[200px] rounded-full -z-1',  // circular
    },
    // Add more badge shapes/colors as needed
  };


  return (
    <>
      <HeaderBar backPage={state.session.isPreorder ? 'presale' : 'address-available'} nextPage={'choose-wifi'} nextPageEnabled={state.session.speed != ''} className="items-center">
        <h1 className="bigger">Welcome to the FAST lane! Pick your speed below!​</h1>
      </HeaderBar>

      {groupOptions.length > 1 && (
        <div className="flex flex-wrap md:flex-nowrap justify-center items-center gap-4 my-2 mb-6 text-center">
          <span style={{fontSize:'1.45rem'}} className="font-bold">
            Tired of price surprises? Lock it down with our price protection picks!
          </span>
          {groupOptions.map(g => (
            <button
              key={g.id}
              type="button"
              onClick={() => selectGroup(g.id)}
              className={`whitespace-nowrap !px-4 !py-2 !text-white rounded-xl text-sm font-medium transition ${
                g.id === state.session.promoGroupID ? '!bg-blue-500' : '!bg-gray-200 text-gray-800'
              }`}
            >
              {g.name}
            </button>
          ))}
        </div>
      )}

      {/* Speed Grid */}
      <div className="flex flex-wrap justify-center gap-6 my-4">
        {availableSpeeds.map((speed) => {
          const isActive = state.session.speed === speed.download;
          const display = displaySpeed(speed.download);

          const displayText = display.text.charAt(0).toUpperCase() + display.text.slice(1)
          
          let displayAmount = parseFloat(speed.displayAmount ?? speed.amount);
          const promo = getApplicablePromo({ speed: speed.download, groupId: state.session.promoGroupID });

          if (promo) {
            const disc = parseFloat(promo.discount.internet || 0);
            const amt = Math.max(0, parseFloat(speed.amount) - disc);
            displayAmount = amt;
          }

          displayAmount = parseFloat(displayAmount.toFixed(2)).toString();

          const badgeStyle = speed.badge ? BADGE_STYLES[speed.badge] : null;

          return (
      <div
        key={speed.download}
        className="relative w-full sm:w-[45%] lg:w-[30%] max-w-[400px] flex flex-col justify-between bg-white p-4 rounded-xl shadow-sm"
      >
      {badgeStyle && (
      <div style={{left: '35px', minWidth: '50%'}}
        className={`absolute top-[-12px] px-3 py-1 text-sm font-bold z-10
          ${badgeStyle.bgColor} ${badgeStyle.textColor} ${badgeStyle.shapeClass}
        `}
      >
        {speed.header}
      </div>
    )}

    {/* Bordered speed card */}
    <div onClick={() => selectSpeed(speed)} className="flex flex-col h-full border-2 border-black rounded-xl p-4 shadow-sm cursor-pointer">
      {/* Top content that grows */}
      <div className="flex-1 flex flex-col items-center text-center">
      <div className="text-lg font-semibold text-blue flex justify-center items-baseline speed-up-down">
        {displayText} Down/{displayText} Up
      </div>

        <div className="flex items-baseline justify-center">
          <span className="text-2xl font-extrabold text-blue">${displayAmount}</span>
          <span className="text-sm text-blue ml-1">/MO</span>
        </div>

        <SpeedDetails 
          className="text-xs text-blue-600 cursor-pointer inline-block mb-2"
          speed={speed}
        />
        <hr className="!my-2 !mb-4 border-1 border-gray-900 w-[70%]" />
        {speed.bullets?.length > 0 && (
          <ul className="text-left text-sm space-y-2 w-full">
            {speed.bullets.map((b, i) => (
              <li key={i} dangerouslySetInnerHTML={{ __html: b }} />
            ))}
          </ul>
        )}
      </div>

      {/* Button fixed to bottom */}
      <div className="mt-4">
        <button          
          className={`w-full !text-white text-sm !font-semibold !mt-2 !py-2 !px-4 !rounded ${
                  isActive ? '!bg-purple' : '!text-black !bg-gray'
                }`}
        >
          {isActive ? (<>Selected</>) : (<>Select</>)}
        </button>
      </div>
    </div>

    {/* Broadband Facts - outside the border */}
    <div className="mt-4 flex justify-center w-full h-[60px] overflow-hidden relative">
      <div
        className={`absolute top-0 left-0 w-full transition-opacity duration-300 ${
          isActive ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        <BroadbandFacts
          id={speed.BroadbandFactsID}
          label="Broadband Facts"
          className="w-full max-w-[90%]"
        />
      </div>
    </div>
  </div>
);


        })}
      </div>
      <MobileNextButton  nextPage={'choose-wifi'} nextPageEnabled={state.session.speed != ''}/>
    </>
  );
};

export default ChooseInternet;
